import { usePaginationWithComponent } from "hooks/usePaginationWithComponent";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from "react";
import { getRequest, patchRequest } from "services/api";
import Swal from "sweetalert2";
import { LIMIT_DATA_PER_PAGE } from "utils/constants";
import { buildQueryString } from "utils/queryHelpers";

const UserContext = createContext();

export const UserProvider = ({ children, roleFilter }) => {
    const [users, setUsers] = useState({
        data: [],
        pagination: null,
        error: null,
        loading: false,
    });

    const [filterOptions, setFilterOptions] = useState({
        search: "",
        currentPage: 1,
        totalPages: 1,
        fingerspot_finger: "",
        fingerspot_face: "",
        fingerspot_vein: "",
        department_id: "",
        orderBy: "sort",
        order: "asc",
    });

    const queryString = buildQueryString({
        search: filterOptions.search || "",
        page: filterOptions.currentPage,
        limit: LIMIT_DATA_PER_PAGE,
        all:1,
        include:"roles,department,position",
        orderBy: filterOptions.orderBy,
        order: filterOptions.order,
        "filter[fingerspot_finger]": filterOptions.fingerspot_finger || "",
        "filter[fingerspot_face]": filterOptions.fingerspot_face || "",
        "filter[fingerspot_vein]": filterOptions.fingerspot_vein || "",
        "filter[department_id]": filterOptions.department_id || "",
    });

    // Handle search input
    const handleSearch = (e) => {
        if (e.key === "Enter") {
            if (!e.target.value.trim()) {
                // Tampilkan toast jika input kosong
                Swal.fire({
                    toast: true,
                    position: "top-end",
                    icon: "warning",
                    title: "Search input cannot be empty!",
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                });
                return;
            }

            console.log("search", e.target.value);
            setFilterOptions((prevState) => ({
                ...prevState,
                search: e.target.value,
            }));
        }
    };

    // Fetch users from API
    const fetchUsers = useCallback(async () => {
        setUsers((prevState) => ({
            ...prevState,
            loading: true,
        }));

        try {
            const response = await getRequest(`/user${queryString}`);
            if (response.success) {
                let filteredData = response.data;
                if (roleFilter) {
                    filteredData = response.data.filter(user =>
                        user.roles && user.roles.some(role => role.name === roleFilter)
                    );
                }
                setUsers({
                    data: filteredData,
                    pagination: response.pagination,
                    error: null,
                    loading: false,
                });
            } else {
                console.log("response else", response);
                // Jika API error, tampilkan alert dengan tombol OK
                Swal.fire({
                    icon: "error",
                    title: "Search Error",
                    text: response.message || "Something went wrong during the search.",
                    confirmButtonText: "OK",
                }).then(() => {
                    // Reload halaman setelah klik OK
                    window.location.reload();
                });
            }
        } catch (error) {
            // Tangani error lain seperti koneksi atau server error
            console.log("Failed to fetch users: ", error);
            Swal.fire({
                icon: "error",
                title: "Search Error",
                text: error.message || "Failed to fetch users.",
                confirmButtonText: "OK",
            }).then(() => {
                // Reload halaman setelah klik OK
                window.location.reload();
            });
        }
    }, [queryString]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handlePageChange = (page) => {
        setFilterOptions((prevState) => ({
            ...prevState,
            currentPage: page,
        }));
    };

    const totalPages = users.pagination?.last_page || 1;

    const Pagination = usePaginationWithComponent({
        totalPages: totalPages,
        currentPage: filterOptions.currentPage,
        onPageChange: handlePageChange,
        visiblePages: 5, // Optional: number of visible pages in pagination (default is 5)
    });

    // Handle filter change
    const handleFilter = (filterName, value) => {
        setFilterOptions((prevState) => ({
            ...prevState,
            currentPage: 1, // Reset to first page when filter changes
            [filterName]: value,
        }));
    };

    // Handle sort
    const handleSort = (field) => {
        setFilterOptions((prevState) => ({
            ...prevState,
            currentPage: 1,
            orderBy: field,
            order: prevState.orderBy === field && prevState.order === "asc" ? "desc" : "asc",
        }));
    };

    // Clear all filters
    const clearFilters = () => {
        setFilterOptions({
            search: "",
            currentPage: 1,
            totalPages: 1,
            fingerspot_finger: "",
            fingerspot_face: "",
            fingerspot_vein: "",
            department_id: "",
            orderBy: "sort",
            order: "asc",
        });
    };

    // Update user sort orders
    const updateUserSorts = async (reorderedUsers) => {
        // Calculate diff to only update changed users
        const usersToUpdate = reorderedUsers.filter(({ id, sort }) => {
            const user = users.data.find(u => u.id === id);
            return user && user.sort !== sort;
        });

        if (usersToUpdate.length === 0) return;

        // Optimistic UI Update
        setUsers((prevState) => {
            const newData = [...prevState.data];
            usersToUpdate.forEach(({ id, sort }) => {
                const userIndex = newData.findIndex(u => u.id === id);
                if (userIndex !== -1) {
                    newData[userIndex] = { ...newData[userIndex], sort };
                }
            });
            // Sort locally
            newData.sort((a, b) => (a.sort || 0) - (b.sort || 0));
            return { ...prevState, data: newData };
        });

        // Show loading toast for background process
        Swal.fire({
            toast: true,
            position: "top-end",
            icon: "info",
            title: "Saving new order...",
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        // API calls in background sequentially to avoid 429 Too Many Requests
        try {
            for (const u of usersToUpdate) {
                const fullUser = users.data.find(usr => usr.id === u.id);
                if (fullUser) {
                    // Omit relational data to avoid validation issues on nested objects
                    const { roles, department, position, created_at, updated_at, ...basicProps } = fullUser;
                    await patchRequest(`/user/${u.id}`, { 
                        ...basicProps, 
                        sort: u.sort 
                    });
                }
            }

            // Success toast
            Swal.fire({
                toast: true,
                position: "top-end",
                icon: "success",
                title: "Order saved successfully",
                showConfirmButton: false,
                timer: 1500,
            });
        } catch (error) {
            console.error("Failed to update sort order", error);
            Swal.fire({
                toast: true,
                position: "top-end",
                icon: "error",
                title: "Failed to save new order!",
                showConfirmButton: false,
                timer: 3000,
            });
            fetchUsers();
        }
    };

    return (
        <UserContext.Provider
            value={{
                users,
                filterOptions,
                setFilterOptions,
                handleSearch,
                handleFilter,
                handleSort,
                clearFilters,
                updateUserSorts,
                refetch: fetchUsers,
                Pagination,
            }}
        >
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);

    if (!context) {
        throw new Error("useUser must be used within UserProvider");
    }

    return context;
};
